import { useImporterTheme } from '@shared/components/csv-importer/useImporterTheme';
import sentry from '@shared/services/sentry';
import { FC } from 'react';
import { ReactSpreadsheetImport } from 'react-spreadsheet-import';
import { Result } from 'react-spreadsheet-import/types/types';
import { Scope1And2CategoryConfig } from '../config';
import { getScope1And2ImporterTranslations } from '../translations';
import { Scope1And2ImportData, Scope1And2ImportRow } from '../types';
import { useScope1And2Fields } from '../fields';
import {
  useScope1And2RowValidator,
  useScope1And2TableValidator,
} from '../utils';

interface CsvImporterScope1And2Props {
  isOpen: boolean;
  onClose: () => void;
  config: Scope1And2CategoryConfig;
  /** The report being imported into; periods resolve against its reporting periods. */
  reportUuid: string;
  handleSubmit: (rows: Scope1And2ImportData, file: File) => Promise<void>;
  /** Excludes the Site column when a single site is chosen up-front. */
  excludeSite?: boolean;
  /** Excludes the Time period column when a single period is chosen up-front. */
  excludePeriod?: boolean;
}

const CsvImporterScope1And2: FC<CsvImporterScope1And2Props> = ({
  isOpen,
  onClose,
  config,
  reportUuid,
  handleSubmit,
  excludeSite = false,
  excludePeriod = false,
}: CsvImporterScope1And2Props) => {
  const theme = useImporterTheme();

  const onSubmit = async (
    data: Result<keyof Scope1And2ImportRow>,
    file: File
  ) => {
    try {
      await handleSubmit(data.validData, file);
    } catch (error) {
      sentry.log(error);
      // Re-throw so react-spreadsheet-import keeps the modal open and shows the
      // error (it renders `error.message`) — preserve the backend's message
      // (e.g. an invalid fuel/unit combination) rather than a generic one.
      throw error instanceof Error
        ? error
        : new Error('Failed importing data, please try again');
    }
  };

  return (
    <ReactSpreadsheetImport<keyof Scope1And2ImportRow>
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      fields={useScope1And2Fields(config, reportUuid, {
        excludeSite,
        excludePeriod,
      })}
      autoMapSelectValues
      allowInvalidSubmit={false}
      rowHook={useScope1And2RowValidator()}
      tableHook={useScope1And2TableValidator(config, {
        excludeSite,
        excludePeriod,
      })}
      customTheme={theme}
      translations={getScope1And2ImporterTranslations(config)}
      isNavigationEnabled
      uploadStepHook={async (data) => {
        if (data.length > 10001) {
          onClose();
          throw new Error(
            'Data limit exceeded: Maximum of 10,000 rows allowed.'
          );
        }
        return data;
      }}
    />
  );
};

export default CsvImporterScope1And2;
